import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import { getRefListNameByID } from "@/util/common_function";
import { searchBookingsModule, fetchBookingDetail } from "./moduleSearchApi";

// Bio + jacket header data — GET bookings/jackets/id/{jacketId}, the same
// endpoint Warrants' "move to folder" flow (WarrantsListDataTable.jsx) uses
// to resolve a Folder ID to a name. Returns the Jackets entity with its
// related Bio row: { jacketId, name, bio: [...], ... }.
export const fetchJacketDetail = (jacketId) =>
  api
    .get(API_ENDPOINTS.warrants.jacketDetailByID + jacketId)
    .then((r) => r?.data);

// Reshapes one booking's search row + full detail into what ArrestsSection
// (JacketReportSections.jsx) renders: the booking header line plus its
// nested Charges and Detentions ("Det. #") tables — matching ASP's Arrests
// section (reports/JacketSummaryNew.asp), which nests exactly those two
// sub-tables under each booking via a client-configurable "TableView"
// (JacketSummary_Bookings) rather than a fixed column list in ASP source.
//
// Charge field mapping verified against a real GET .../bookingNumber/id/
// {id} payload (not just component source): charges[] rows carry
// chargeDescriptionID/chargeLevelID/offenderDispositionID — all numeric ref
// IDs, not plain text — resolved the same way ChargesList/index.jsx and
// AddCharges/CourtInformation.jsx already do. offenderDispositionID in
// particular *is* ASP's "Disposition" column (label "Current Disposition",
// ref list "OffenderDisposition") — an earlier pass here wrongly called
// this a gap because chargeDescription/disposition don't exist as plain
// fields; they're just IDs needing the same resolution as everything else.
const buildArrestRecord = (row, detail) => {
  const arrestReport = detail?.arrestReports?.[0];

  const charges = (detail?.charges || []).map((c) => ({
    id: c?.chargeID,
    chargeDescription: getRefListNameByID("Charges", c?.chargeDescriptionID),
    level: getRefListNameByID("Charge_Level", c?.chargeLevelID),
    disposition: getRefListNameByID(
      "OffenderDisposition",
      c?.offenderDispositionID,
      false,
      true,
    ),
  }));

  // Each detention can have multiple detentionsArrestReports entries (housing
  // moves); DetentionList/index.jsx flattens the same way, one row per entry.
  const detentions = (detail?.detentions || []).flatMap((d) =>
    (d?.detentionsArrestReports?.length
      ? d.detentionsArrestReports
      : [{}]
    ).map((da, idx) => ({
      id: `${detail?.bookingNumber || row?.bookingNumber || ""}${d?.detentionSuffix || ""}${idx || ""}`,
      start: [da?.incarcerationDate, da?.incarcerationTime]
        .filter(Boolean)
        .join(" "),
      agency: da?.housingAgency,
      end: [da?.releaseDate, da?.releaseTime].filter(Boolean).join(" "),
      releaseReason: getRefListNameByID("ChargeClearance", da?.releaseReasonId),
    })),
  );

  return {
    bookingNumber: detail?.bookingNumber || row?.bookingNumber,
    incarcerationDate: arrestReport?.incarcerationDate || row?.incarcerationDate,
    agency: arrestReport?.agency,
    charges,
    detentions,
  };
};

// Hold record → ASP's Holds columns (Hold Reason/Hold For/Hold Contact/Hold
// Contact Phone/Expires Date/Expires Time/Comments). Field names and the
// "Hold_Reason" ref list confirmed against BookingHolds/BookingHoldsForm.jsx
// — expiresDate/expiresTime are genuinely separate fields on the raw record
// (the form's single "Expires Date & Time" picker just recombines them for
// editing), so no date-split logic is needed here.
const buildHoldRecord = (hold) => ({
  holdReason: getRefListNameByID("Hold_Reason", hold?.holdReason, false, true),
  holdFor: hold?.holdFor,
  holdContact: hold?.holdContact,
  holdContactPhone: hold?.holdContactPhone,
  expiresDate: hold?.expiresDate,
  expiresTime: hold?.expiresTime,
  comments: hold?.comments,
});

// Victim notification record → ASP's Victim Notifications columns (Status/
// Name/Booking Number/Comments). The notified party's own bio rides along
// nested under jackets[0].bio[0] on each record, same shape as everywhere
// else. bookingNumber is passed in rather than read off the record itself —
// the record only carries the internal bookingID, and since this is read
// straight off one booking's own detail response, that booking's own
// (human-readable) bookingNumber is already known.
const buildVictimNotificationRecord = (vn, bookingNumber) => {
  const victimBio = vn?.jackets?.[0]?.bio?.[0];
  const givenNames = [victimBio?.fName, victimBio?.mName]
    .filter(Boolean)
    .join(" ");
  const name = victimBio?.lName
    ? [victimBio.lName, givenNames].filter(Boolean).join(", ")
    : givenNames;

  return {
    status: vn?.status,
    name,
    bookingNumber,
    comments: vn?.comments,
  };
};

// Inmate Associates → ASP's Inmate Associates columns (Booking_Number/
// Associate Type/Associate Name/JacketID). This is the *forward* direction
// only — associates this booking's jacket itself declared, via
// bookingsJacketsJackets → jacketsJackets[] → jackets[0] (the associate's
// own folder/bio). ASP also has a reverse "Associated with Inmates" query
// (who lists *this* jacket as their associate) — there's no equivalent
// lookup available from this booking's own detail response, so that
// section is left as a real gap rather than half-faked here.
const buildInmateAssociateRecords = (bookingsJacketsJackets, bookingNumber) =>
  (bookingsJacketsJackets || []).flatMap((item) =>
    (item?.jacketsJackets || []).map((jj) => ({
      bookingNumber,
      associateType: getRefListNameByID(
        "Inmate_Associate_Type",
        jj?.associateTypeID,
        false,
        true,
      ),
      associateName: jj?.jackets?.[0]?.name,
      jacketId: jj?.jackets?.[0]?.jacketId,
    })),
  );

// Arrests + Holds + Victim Notifications + Inmate Associates for this
// jacket — all four ride along on the same per-booking detail fetch (GET
// .../bookingNumber/id/{id}, the same call BookingSummaryReport uses), so
// pulling all four costs one request per booking rather than one per
// section. Starts from the same bookings/search call Super Search's own
// Bookings module hits (filtered to this jacket via the "jacketId"
// filterKey) since search alone doesn't return any of this.
export const fetchJacketBookingSections = (jacketId) =>
  searchBookingsModule({
    requestFilter: [{ filterKey: "jacketId", value: String(jacketId) }],
    pagination: { pageIndex: 0, pageSize: 100 },
  })
    .then((data) => data?.rows || [])
    .then((rows) =>
      Promise.all(
        rows.map((row) =>
          fetchBookingDetail(row.bookingNumber)
            .then((detail) => ({ row, detail }))
            .catch(() => ({ row, detail: null })),
        ),
      ),
    )
    .then((pairs) => {
      const arrests = [];
      const holds = [];
      const victimNotifications = [];
      const inmateAssociates = [];

      pairs.forEach(({ row, detail }) => {
        const bookingNumber = detail?.bookingNumber || row?.bookingNumber;

        arrests.push(buildArrestRecord(row, detail));
        (detail?.holds || []).forEach((h) => holds.push(buildHoldRecord(h)));
        (detail?.victimNotifications || []).forEach((vn) =>
          victimNotifications.push(
            buildVictimNotificationRecord(vn, bookingNumber),
          ),
        );
        inmateAssociates.push(
          ...buildInmateAssociateRecords(
            detail?.bookingsJacketsJackets,
            bookingNumber,
          ),
        );
      });

      return { arrests, holds, victimNotifications, inmateAssociates };
    })
    .catch(() => ({
      arrests: [],
      holds: [],
      victimNotifications: [],
      inmateAssociates: [],
    }));

// Warrants for this jacket — GET warrant/jacket/{jacketId}. Note: this
// returns at most one WarrantRoot (WarrantSingleEntityResponse wraps a
// single Optional<WarrantRoots>), not a full history of every warrant this
// person has ever had — that root can itself hold multiple Warrants rows,
// which is what we list here.
export const fetchJacketWarrants = (jacketId) =>
  api
    .get(API_ENDPOINTS.warrants.getWarrantByFolderId + jacketId)
    .then((r) => {
      const root = r?.data?.warrantRoot;
      const warrants = root?.warrants || [];
      return warrants.map((w) => ({
        ...w,
        warrantRootID: root?.warrantRootID,
      }));
    })
    .catch(() => []);
